import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

export const mono = { fontFamily: 'var(--v2-font-mono)' } as const

/** `$idOrCode` routes aren't in the typed `to` union; resolved at runtime by the real route tree. */
export function SLink({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  return (
    <Link to={to as '/locations'} className={className}>
      {children}
    </Link>
  )
}

export const linkCls = 'text-[var(--color-accent)] hover:underline'
export const btnCls =
  'inline-flex h-7 items-center rounded-[4px] border border-[var(--color-border)] px-2.5 text-[12px] hover:bg-[var(--color-surface-2)] focus-visible:outline-1 focus-visible:outline-[var(--color-accent)]'

type Tone = 'ok' | 'bad' | 'off' | 'warn'
const TONE_COLOR: Record<Tone, string> = {
  ok: 'var(--v2-ok)',
  bad: 'var(--v2-bad, #f0616d)',
  off: 'var(--v2-off)',
  warn: 'var(--v2-warn, #e5a23c)',
}

export function Dot({ tone }: { tone: Tone }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-1.5 shrink-0 rounded-full"
      style={{ background: TONE_COLOR[tone] }}
    />
  )
}

export function statusTone(s: string): Tone {
  const u = s.toUpperCase()
  if (u === 'UP' || u === 'ONLINE' || u === 'ACTIVE' || u === 'PROVISIONED') return 'ok'
  if (u === 'DOWN' || u === 'OFFLINE') return 'bad'
  if (u === 'DEGRADED' || u === 'ALERTING') return 'warn'
  return 'off'
}

export function StatusText({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Dot tone={statusTone(status)} />
      <span>{status}</span>
    </span>
  )
}

export function Crumbs({ idOrCode, trail }: { idOrCode: string; trail: 'detail' | 'network' | 'wan' }) {
  const base = `/locations/${encodeURIComponent(idOrCode)}`
  const hover = 'hover:text-[var(--color-fg)]'
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-[12px] text-[var(--color-muted)]"
      style={mono}
    >
      <Link to="/locations" className={hover}>
        locations
      </Link>
      <span aria-hidden="true">/</span>
      {trail === 'detail' ? (
        <span className="text-[var(--color-fg)]">{idOrCode}</span>
      ) : (
        <SLink to={base} className={hover}>
          {idOrCode}
        </SLink>
      )}
      {trail === 'network' && (
        <>
          <span aria-hidden="true">/</span>
          <span className="text-[var(--color-fg)]">network</span>
        </>
      )}
      {trail === 'wan' && (
        <>
          <span aria-hidden="true">/</span>
          <SLink to={`${base}/network`} className={hover}>
            network
          </SLink>
          <span aria-hidden="true">/</span>
          <span className="text-[var(--color-fg)]">wan-settings</span>
        </>
      )}
    </nav>
  )
}

export function Bar({
  crumbs,
  title,
  meta,
  right,
}: {
  crumbs: ReactNode
  title: ReactNode
  meta?: ReactNode
  right?: ReactNode
}) {
  return (
    <header className="shrink-0 border-b border-[var(--color-border)] px-4 py-2">
      {crumbs}
      <div className="mt-1 flex min-h-7 flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="text-[14px] font-semibold tracking-[-0.01em]">{title}</h1>
        {meta}
        <div className="ml-auto flex items-center gap-2">{right}</div>
      </div>
    </header>
  )
}

export function Panel({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section
      className="border border-[var(--color-border)] bg-[var(--color-surface)]"
      style={{ borderRadius: 'var(--radius)' }}
    >
      <div className="flex h-7 items-center border-b border-[var(--color-border)] px-3">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]">{title}</h2>
        {aside && <div className="ml-auto text-[11px]">{aside}</div>}
      </div>
      {children}
    </section>
  )
}

/** Dense key/value rows, one hairline each. */
export function KV({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl>
      {items.map(([k, v]) => (
        <div
          key={k}
          className="flex h-7 items-center gap-3 border-b border-[var(--color-border)] px-3 text-[12px] last:border-b-0"
        >
          <dt className="w-28 shrink-0 text-[var(--color-muted)]">{k}</dt>
          <dd className="min-w-0 truncate" style={mono}>
            {v === null || v === undefined || v === '' ? <span className="text-[var(--color-muted)]">-</span> : v}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function Note({ children, error }: { children: ReactNode; error?: boolean }) {
  return (
    <div
      role={error ? 'alert' : 'status'}
      className="px-4 py-3 text-[12px] text-[var(--color-muted)]"
      style={error ? { color: 'var(--color-fg)', boxShadow: 'inset 2px 0 0 var(--color-accent)' } : undefined}
    >
      {children}
    </div>
  )
}

const SKEL_KEYS = ['a', 'b', 'c', 'd']

export function SkelPanels({ n = 3 }: { n?: number }) {
  return (
    <div className="grid gap-3 p-4" aria-busy="true">
      {SKEL_KEYS.slice(0, n).map((k) => (
        <div key={k} className="grid h-24 content-center gap-3 border border-[var(--color-border)] px-3">
          <span className="block h-2 w-1/4 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
          <span className="block h-2 w-full animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
          <span className="block h-2 w-2/3 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
        </div>
      ))}
    </div>
  )
}

export function Stat({ label, value, tone }: { label: string; value: number | string; tone?: 'ok' | 'bad' }) {
  return (
    <div className="border-r border-[var(--color-border)] px-3 py-2 last:border-r-0">
      <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">{label}</div>
      <div
        className="text-[20px] leading-7 font-medium"
        style={{ ...mono, color: tone ? TONE_COLOR[tone] : undefined }}
      >
        {value}
      </div>
    </div>
  )
}
